import { registerDismissalStore, type DismissalStore } from '@galapagos/design-system/web';
import { ensureAuthTeardownParticipant, type AuthTeardownParticipant } from './auth-teardown';

/**
 * The tab-scoped key the web app keeps closed design-system cards under (the
 * signup rewards card), beside the signup flow's own key, so a closed card
 * stays closed on a refresh. It holds card keys only, never member data.
 */
export const SIGNUP_DISMISSALS_STORAGE_KEY = 'galapagos.signup.dismissals';

export interface SessionDismissalStore extends DismissalStore {
  /** Forgets every dismissal, in memory and in storage. */
  clear(): void;
}

function readStored(): string[] {
  try {
    const parsed: unknown = JSON.parse(
      window.sessionStorage.getItem(SIGNUP_DISMISSALS_STORAGE_KEY) ?? '[]',
    );
    return Array.isArray(parsed) && parsed.every((key) => typeof key === 'string') ? parsed : [];
  } catch {
    return [];
  }
}

function writeStored(keys: ReadonlySet<string>): void {
  try {
    if (keys.size === 0) window.sessionStorage.removeItem(SIGNUP_DISMISSALS_STORAGE_KEY);
    else window.sessionStorage.setItem(SIGNUP_DISMISSALS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // Unwritable storage: the dismissal still holds for this page load.
  }
}

/**
 * Dismissals for this tab: answered from memory, seeded from and mirrored to
 * session storage. Off the browser (static prerender) nothing is dismissed.
 */
export function createSessionDismissalStore(): SessionDismissalStore {
  const dismissed = new Set<string>(typeof window === 'undefined' ? [] : readStored());
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());
  return {
    isDismissed: (key) => dismissed.has(key),
    dismiss: (key) => {
      dismissed.add(key);
      writeStored(dismissed);
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    clear: () => {
      dismissed.clear();
      writeStored(dismissed);
      notify();
    },
  };
}

let installed: SessionDismissalStore | null = null;

/** Closed cards are forgotten when the session ends, so the next member in the tab sees them. */
export const signupDismissalsTeardownParticipant: AuthTeardownParticipant = {
  id: 'signup-dismissals',
  clear: () => {
    if (installed) installed.clear();
    else writeStored(new Set());
  },
};

/**
 * Makes the design system remember dismissals for the tab, and joins the
 * session-boundary teardown. Idempotent; call it at module load, before any
 * dismissible renders.
 */
export function installSessionDismissalStore(): void {
  if (!installed) {
    installed = createSessionDismissalStore();
    registerDismissalStore(installed);
  }
  ensureAuthTeardownParticipant(signupDismissalsTeardownParticipant);
}
